import React, { useCallback, useEffect, useRef, useState } from 'react'
import NetworkView, { NetworkView3Ref } from '../networkView/NetworkView'
import {
    CandidateLiteDto,
    PresentCandidateValues,
} from '../../api/role/models/CandidateDto'
import { MapExtra } from '../../utils/MapExtra'
import { PersonDto } from '../../api/person/models/PersonDto'
import { CompanyDto } from '../../api/company/models/CompanyDto'
import { ResolvedPersonLiteDto } from '../../api/network/models/getNetworkScopedDtos'
import { PersonType, SDManager } from '../networkView/SDManager'
import companyApi from '../../api/company/CompanyApi'
import Button from '@/uiV2/common/form/inputs/Button'

type CompanyContextMenuState = {
    companyId: string
    companyName: string
    x: number
    y: number
}

type Props = {
    resolvedPersons: ResolvedPersonLiteDto[]
    networkLinksPersonCache: MapExtra<string, PersonDto>
    candidatesPersonCache: MapExtra<string, PersonDto>
    companyCache: MapExtra<string, CompanyDto>
    hasRole?: boolean
    onCandidateSelection?: (candidateId: string) => void
    candidatePresentById?: Map<string, PresentCandidateValues | undefined>
}

function HelixSearchNetworkView({
    resolvedPersons,
    networkLinksPersonCache,
    candidatesPersonCache,
    companyCache,
    hasRole = false,
    onCandidateSelection,
    candidatePresentById,
}: Props): React.ReactNode {
    const networkViewRef = useRef<NetworkView3Ref | null>(null)
    const [sdManager, setSdManager] = useState<SDManager | null>(null)
    const prevResolvedPersonIdsRef = useRef<string[]>([])
    const [contextMenu, setContextMenu] =
        useState<CompanyContextMenuState | null>(null)
    const [excludedCompanyIds, setExcludedCompanyIds] = useState<string[]>([])
    const hiddenCompanyIdsRef = useRef(new Set<string>())
    const hiddenPersonIdsRef = useRef(new Set<string>())
    const hiddenLinkKeysRef = useRef(new Set<string>())
    const [hasHiddenNodes, setHasHiddenNodes] = useState(false)

    const handleNetworkViewRef = useCallback(
        (node: NetworkView3Ref | null) => {
            networkViewRef.current = node
            if (node?.sdManager && !sdManager) {
                setSdManager(node.sdManager)
            }
        },
        [sdManager],
    )

    const handleCompanyContextMenu = useCallback(
        (companyId: string, companyName: string, event: MouseEvent) => {
            event.preventDefault()
            setContextMenu({
                companyId,
                companyName,
                x: event.clientX,
                y: event.clientY,
            })
        },
        [],
    )

    const handleExcludeCompany = useCallback((companyId: string) => {
        setContextMenu(null)
        setExcludedCompanyIds((prev) =>
            prev.includes(companyId) ? prev : [...prev, companyId],
        )
    }, [])

    useEffect(() => {
        if (!sdManager || !onCandidateSelection) return

        sdManager.registerOnPersonSelected((person, personType) => {
            if (personType === PersonType.CANDIDATE && person?.id) {
                onCandidateSelection(person.id)
            }
        })
        sdManager.registerOnPersonHovered((person, personType) => {
            if (personType === PersonType.CANDIDATE && person?.id) {
                onCandidateSelection(person.id)
            }
        })
    }, [sdManager, onCandidateSelection])

    useEffect(() => {
        if (!sdManager) return
        sdManager.registerOnCompanyContextMenu(handleCompanyContextMenu)
    }, [sdManager, handleCompanyContextMenu])

    useEffect(() => {
        if (!contextMenu) return
        const handleClickOutside = () => setContextMenu(null)
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setContextMenu(null)
            }
        }
        window.addEventListener('click', handleClickOutside)
        window.addEventListener('keydown', handleEscape)
        return () => {
            window.removeEventListener('click', handleClickOutside)
            window.removeEventListener('keydown', handleEscape)
        }
    }, [contextMenu])

    useEffect(() => {
        if (!sdManager || !networkViewRef.current) return

        const currentIds = resolvedPersons.map((p) => p.personId).sort()
        const previousIds = prevResolvedPersonIdsRef.current

        const isSame =
            currentIds.length === previousIds.length &&
            currentIds.every((id, idx) => id === previousIds[idx])

        if (isSame) return
        prevResolvedPersonIdsRef.current = currentIds

        // this is a stick plaster fix. For some reason the companyCache is sometimes
        //  missing companies. I've allowed the SDManager to call into the API via this callback
        //  if a company is missing.
        sdManager.setCaches(
            candidatesPersonCache,
            companyCache,
            async (companyId: string) => {
                // the company is not in the cache, go and get it from the api
                const result = await companyApi.getCompanies({
                    companyIds: [companyId],
                })
                if (
                    !result ||
                    !result.companies ||
                    result.companies.length === 0
                ) {
                    throw new Error(
                        `Company with ID ${companyId} not found in the API response. HelixSearchNetworkView sdManager company getter.`,
                    )
                }
                return result.companies[0]
            },
        )
        sdManager.setShowStatusInfo(hasRole)
        sdManager.reset()

        const connectedNetworkLinksIds = new Set<string>()
        resolvedPersons.forEach((person) => {
            person.connectionDetails.forEach((connection) => {
                connectedNetworkLinksIds.add(connection.seedPersonId)
            })
        })

        const connectedNetworkLinksPersons = Array.from(
            networkLinksPersonCache.values(),
        ).filter((person) => connectedNetworkLinksIds.has(person.id))

        sdManager.initialiseSeedPersons(connectedNetworkLinksPersons, [])

        const candidates: CandidateLiteDto[] = resolvedPersons.map(
            (person) => ({
                candidatePersonId: person.personId,
                present: candidatePresentById?.get(person.personId),
                contacted: false,
                connections: person.connectionDetails.map((connection) => ({
                    connectedPersonId: connection.seedPersonId,
                    sharedWorkHistory: connection.sharedWorkHistory,
                })),
                companyConnections: person.companyConnectionDetails.map(
                    (company) => ({
                        connectedCompanyId: company.companyId,
                    }),
                ),
                rankingScore: 0,
                closenessScore: 0,
                jobRelevanceScore: 0,
                jobRelevanceRawScore: 0,
                jobRelevanceJobTitle: '',
            }),
        )

        sdManager.addCandidates(candidates)
    }, [
        resolvedPersons,
        sdManager,
        networkLinksPersonCache,
        candidatesPersonCache,
        companyCache,
        hasRole,
        candidatePresentById,
    ])

    useEffect(() => {
        if (!sdManager || excludedCompanyIds.length === 0) {
            return
        }

        const idsToHide = new Set(excludedCompanyIds)

        if (idsToHide.size === 0) {
            return
        }

        for (const companyId of idsToHide) {
            const hideResult = sdManager.hideCompanyConnections(companyId)
            if (!hideResult) {
                continue
            }

            hiddenCompanyIdsRef.current.add(companyId)

            for (const personId of hideResult.hiddenPersonIds) {
                hiddenPersonIdsRef.current.add(personId)
            }

            for (const linkKey of hideResult.linkKeys) {
                hiddenLinkKeysRef.current.add(linkKey)
            }
        }

        setHasHiddenNodes(
            hiddenCompanyIdsRef.current.size > 0 ||
                hiddenPersonIdsRef.current.size > 0 ||
                hiddenLinkKeysRef.current.size > 0,
        )
    }, [sdManager, excludedCompanyIds])

    const handleRevealHiddenNodes = useCallback(() => {
        if (!sdManager) return

        const companyIds = Array.from(hiddenCompanyIdsRef.current)
        if (companyIds.length > 0) {
            sdManager.showCompanyNodes(companyIds)
        }

        const personIds = Array.from(hiddenPersonIdsRef.current)
        if (personIds.length > 0) {
            sdManager.showPersonNodes(personIds)
        }

        const linkKeys = Array.from(hiddenLinkKeysRef.current)
        if (linkKeys.length > 0) {
            sdManager.showLinks(linkKeys)
        }

        sdManager.revealAllHiddenNodesAndLinks()

        hiddenCompanyIdsRef.current.clear()
        hiddenPersonIdsRef.current.clear()
        hiddenLinkKeysRef.current.clear()
        setHasHiddenNodes(false)
        setExcludedCompanyIds([])
        setContextMenu(null)
    }, [sdManager])

    useEffect(() => {
        if (!sdManager || !candidatePresentById) return

        candidatePresentById.forEach((present, candidatePersonId) => {
            sdManager.candidateChanged({
                candidatePersonId,
                present,
            })
        })
    }, [candidatePresentById, sdManager])

    return (
        <div className="relative">
            <NetworkView ref={handleNetworkViewRef} />
            {hasHiddenNodes && (
                <div className="absolute left-4 bottom-4 z-20">
                    <Button
                        type="button"
                        style="secondary"
                        size="small"
                        onClick={handleRevealHiddenNodes}
                    >
                        Show hidden nodes
                    </Button>
                </div>
            )}
            {contextMenu && (
                <div
                    className="fixed z-50 min-w-[180px] rounded-lg border border-gray-200 bg-white text-sm shadow-lg"
                    style={{
                        top: contextMenu.y,
                        left: contextMenu.x,
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onContextMenu={(event) => event.stopPropagation()}
                >
                    <button
                        type="button"
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() =>
                            handleExcludeCompany(contextMenu.companyId)
                        }
                    >
                        Exclude company
                    </button>
                </div>
            )}
        </div>
    )
}

export default HelixSearchNetworkView
